import compact from "lodash/compact";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useActivityVacancies } from "PFCore/hooks/queries/activity";
import { useActivityRankedShortlists } from "PFCore/hooks/queries/shortlists/use_activity_ranked_shortlists";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { Activity } from "PFTypes/activity";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { OpenVacancyItem, ShortlistWithScores, SuggestedWmTableItem } from "./types";

type UseSuggestedShortlistItemsReturn = {
  tableData: SuggestedWmTableItem[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

export const useSuggestedShortlistItems = (activity: Activity): UseSuggestedShortlistItemsReturn => {
  const { t } = useTranslation();
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();

  const {
    data: shortlists,
    isLoading: isShortlistsLoading,
    isFetching: isShortlistsFetching,
    error: shortlistsError,
    isError: isShortlistsError
  } = useActivityShortlists(activity.id);

  const {
    data: rankedProfiles,
    isLoading: isRankedProfilesLoading,
    error: rankedProfilesError,
    isError: isRankedProfilesError
  } = useActivityRankedShortlists({
    activityId: activity.id
  });

  const {
    data: vacanciesData,
    isLoading: isVacanciesLoading,
    isFetching: isVacanciesFetching,
    error: vacanciesError,
    isError: isVacanciesError
  } = useActivityVacancies({ activityId: activity.id, perPage: PER_PAGE_UNPAGINATED });

  useEffect(() => {
    compact([shortlistsError, rankedProfilesError, vacanciesError]).forEach((error) => {
      const errorMessage = getFirstError(error);
      showGrowl({
        message: errorMessage || t("somethingWentWrong"),
        kind: "error"
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistsError, rankedProfilesError, vacanciesError]);

  const isLoading = isShortlistsLoading || isRankedProfilesLoading || isVacanciesLoading;
  const isFetching = isShortlistsFetching || isVacanciesFetching;
  const isError = isShortlistsError || isRankedProfilesError || isVacanciesError;

  const scoresMap = new Map((rankedProfiles?.entries ?? []).map((entry) => [entry.id, entry.scores]));

  const suggestedShortlists = (shortlists?.entries ?? [])
    .filter((shortlist) => !!shortlist.suggested)
    .map<ShortlistWithScores>((shortlist) => ({
      ...shortlist,
      scores: scoresMap.get(shortlist.profile.id) ?? null
    }));
  const closedVacancyProfiles = new Set(
    vacanciesData?.entries
      .filter(({ state }) => state === "closed")
      .flatMap(({ profileIds }) => profileIds) ?? []
  );
  const suggestedShortlistsNotFillingVacancy = suggestedShortlists.filter(
    ({ profile }) => !closedVacancyProfiles.has(profile.id)
  );

  const openVacanciesCount = activity.vacancies ? activity.vacancies.total - activity.vacancies.closed : 0;
  const openVacancyItemsCount = Math.max(0, openVacanciesCount - suggestedShortlistsNotFillingVacancy.length);
  const openVacancyItems: OpenVacancyItem[] = Array.from({ length: openVacancyItemsCount }, () => ({
    openVacancy: true
  }));

  const tableData: SuggestedWmTableItem[] = [...suggestedShortlists, ...openVacancyItems];

  return { tableData, isLoading, isFetching, isError };
};
